import type { IFilter } from "../api/models";

// PORT OF THE ANGULAR `AdvancedFilter` / `FilterRange` / `FilterPlayer` MODELS
// (`src/app/_shared/_models/data-view.model.ts`) AND OF THE `createFilters()` /
// `checkIfCanSaveApplyFilter()` RULES IN `advanced-filters.component.ts` AND
// `my-filters.component.ts`. THE SHAPES ARE SAVED VERBATIM (AS JSON) BY
// `/ht/create-active-session-filter`, SO FIELD NAMES MUST NOT CHANGE.

// `FilterType` IN `filter-range.component.ts`
export const FilterType = {
  PLAYER: "PLAYER",
  DURATION: "DURATION",
  GAINS: "GAINS",
  BET: "BET",
  AVERAGE_BET: "AVERAGE_BET",
  THEO: "THEO",
} as const;

export interface FilterRange {
  type: string;
  enabled: boolean;
  min: number;
  max: number;
}

export interface FilterPlayer {
  type: string;
  enabled: boolean;
  hostIds: number[] | null;
  unhosted: boolean;
  carded: boolean;
  uncarded: boolean;
}

export interface AdvancedFilter {
  role: string;
  label: string;
  type: string;
  active: boolean;
  player: FilterPlayer;
  duration: FilterRange;
  gains: FilterRange;
  bet: FilterRange;
  averageBet: FilterRange;
  theo: FilterRange;
  filters: IFilter[];
}

export type RangeKey = "duration" | "gains" | "bet" | "averageBet" | "theo";

// `setDefaultAdvancedFilter()` IN THE ANGULAR IN PLAY PAGE (`setDefaultQuickFilter()` IS THE
// SAME OBJECT WITH `type: "quick"` AND AN EMPTY LABEL)
export function defaultAdvancedFilter(): AdvancedFilter {
  return {
    role: "",
    label: "Advanced Filter",
    type: "advanced",
    filters: [],
    active: false,
    player: { type: FilterType.PLAYER, enabled: false, hostIds: null, unhosted: false, carded: false, uncarded: false },
    duration: { type: FilterType.DURATION, enabled: false, min: 0, max: 24 },
    gains: { type: FilterType.GAINS, enabled: false, min: 0, max: 500 },
    bet: { type: FilterType.BET, enabled: false, min: 0, max: 200 },
    averageBet: { type: FilterType.AVERAGE_BET, enabled: false, min: 0, max: 25 },
    theo: { type: FilterType.THEO, enabled: false, min: 0, max: 500 },
  };
}

// A DEEP COPY, SO A DRAFT BEING EDITED NEVER MUTATES THE APPLIED FILTER (ANGULAR EDITS THE
// APPLIED OBJECT IN PLACE)
export function cloneFilter(f: AdvancedFilter): AdvancedFilter {
  return JSON.parse(JSON.stringify(f)) as AdvancedFilter;
}

// `checkIfCanSaveApplyFilter()`: SAVE AND APPLY ARE BOTH ENABLED ONCE THE PLAYER SECTION IS
// ON WITH A HOST, UNHOSTED, CARDED OR UNCARDED CHOICE, OR ANY RANGE IS ON
export function canApply(f: AdvancedFilter): boolean {
  const p = f.player;
  if (p?.enabled && ((p.hostIds?.length ?? 0) > 0 || p.unhosted || p.carded || p.uncarded)) return true;
  return (["duration", "gains", "bet", "averageBet", "theo"] as const).some((k) => f[k]?.enabled === true);
}

// THE SELECT-BUTTON OPTIONS OF `filter-player`
export const PLAYER_OPTIONS = [
  { label: "Carded", value: "carded" },
  { label: "Unhosted", value: "unhosted" },
  { label: "Uncarded", value: "uncarded" },
] as const;
export type PlayerOption = (typeof PLAYER_OPTIONS)[number]["value"];

// `onEnabledChanged` IN `filter-player`: TURNING THE SECTION OFF DROPS THE HOSTS (AND THE
// OPTION CHOICE, WHICH ANGULAR KEEPS ONLY IN THE VIEW)
export function setPlayerEnabled(p: FilterPlayer, enabled: boolean): FilterPlayer {
  return enabled ? { ...p, enabled } : { ...p, enabled, hostIds: [], carded: false, unhosted: false, uncarded: false };
}

// `onOptionsChange`
export function setPlayerOptions(p: FilterPlayer, options: readonly PlayerOption[]): FilterPlayer {
  return { ...p, carded: options.includes("carded"), unhosted: options.includes("unhosted"), uncarded: options.includes("uncarded") };
}

// `onHostsChange`: PICKING ANY HOST CLEARS UNHOSTED
export function setPlayerHosts(p: FilterPlayer, hostIds: number[]): FilterPlayer {
  return { ...p, hostIds, unhosted: hostIds.length > 0 ? false : p.unhosted };
}

// THE OPTIONS SHOWN SELECTED, DERIVED FROM THE FILTER (ANGULAR KEEPS A SEPARATE VIEW ARRAY THAT
// FORGETS THE CHOICE WHEN THE TAB IS REOPENED)
export function selectedPlayerOptions(p: FilterPlayer): PlayerOption[] {
  return PLAYER_OPTIONS.map((o) => o.value).filter((v) => p[v]);
}

// `UiService.toMilliseconds(0, UiService.toMinutes(hours), 0)`
const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;

// DOLLAR RANGES ARE SENT AS CENTS BY APPENDING "00" TO THE WHOLE-DOLLAR TEXT (SO 0 IS "000")
function dollarClause(field: string, r: FilterRange, condition: IFilter["condition"]): IFilter {
  const same = r.min === r.max;
  return {
    field,
    value: same ? r.max.toFixed() + "00" : r.min.toFixed() + "00," + r.max.toFixed() + "00",
    valueDataType: "number",
    type: same ? "=" : "range",
    condition,
  };
}

// WHICH COMPONENT'S `createFilters()` TO REPRODUCE. THEY DIFFER IN ONE PLACE: THE ADVANCED TAB
// SENDS ONE `host_id` CLAUSE WITH THE IDS COMMA-JOINED, WHILE "MY FILTERS" SENDS ONE CLAUSE PER
// HOST. BOTH ARE COPIED AS THEY ARE.
export type CreateVariant = "advanced" | "saved";

// `createFilters()`. `playerOnly` IS THE ADVANCED TAB'S `showFilterPlayerOnly` (FALSE ON IN PLAY):
// IT DROPS THE OPEN-SESSION CLAUSE AND JOINS THE HOST CLAUSE WITH "OR".
export function createFilters(af: AdvancedFilter, variant: CreateVariant, playerOnly = false): IFilter[] {
  const out: IFilter[] = [];
  const next = (): IFilter["condition"] => (out.length > 0 ? "AND" : null);

  if (variant === "saved" || !playerOnly) {
    out.push({ field: "end_time", value: "", valueDataType: "datetime", type: "IS", condition: "AND" });
  }

  const p = af.player;
  if (p?.enabled) {
    const hostIds = p.hostIds ?? [];
    if (variant === "advanced" && hostIds.length > 0) {
      out.push({ field: "host_id", value: hostIds.join(","), valueDataType: "number", type: "=", condition: playerOnly ? "OR" : "AND" });
    }
    if (variant === "saved") {
      for (const id of hostIds) {
        if (id) out.push({ field: "host_id", value: id.toFixed(), valueDataType: "number", type: "=", condition: next() });
      }
    }
    if (p.unhosted) out.push({ field: "host_id", value: "", valueDataType: "number", type: "IS NULL", condition: next() });
    if (p.carded) out.push({ field: "is_carded", value: "true", valueDataType: "bool", type: "=", condition: next() });
    if (p.uncarded) out.push({ field: "is_carded", value: "false", valueDataType: "bool", type: "=", condition: next() });
  }

  if (af.duration?.enabled) {
    const start = hoursToMs(af.duration.min);
    const end = hoursToMs(af.duration.max);
    const same = start === end;
    out.push({
      field: "duration",
      value: same ? end.toFixed() : start + "," + end,
      valueDataType: "number",
      type: same ? "=" : "range",
      condition: next(),
    });
  }
  if (af.gains?.enabled) out.push(dollarClause("gains", af.gains, next()));
  if (af.bet?.enabled) out.push(dollarClause("coin_in", af.bet, next()));
  if (af.averageBet?.enabled) out.push(dollarClause("avg_bet", af.averageBet, next()));
  if (af.theo?.enabled) out.push(dollarClause("theo_win", af.theo, next()));
  return out;
}

// `applyAdvancedFilter()` / `applyFilter()`: THE FILTER WITH ITS CLAUSES BUILT
export function withFilters(af: AdvancedFilter, variant: CreateVariant): AdvancedFilter {
  return { ...cloneFilter(af), filters: createFilters(af, variant) };
}

// ONE ROW OF `/ht/get-active-session-filters`
export interface SavedFilter {
  id: number;
  name: string;
  user_id: number;
  advanced_filter: string;
}

// `JSON.parse(maf.advanced_filter)`, WITH THE SHAPE CHECKED: A SAVED ROW THAT DOES NOT PARSE AS
// AN ADVANCED FILTER IS REFUSED RATHER THAN APPLIED HALF-FORMED
export function parseSavedFilter(json: string): AdvancedFilter | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const base = defaultAdvancedFilter();
  const o = raw as Record<string, unknown>;
  const range = (k: RangeKey): FilterRange => {
    const v = o[k];
    if (!v || typeof v !== "object") return base[k];
    const r = v as Record<string, unknown>;
    return {
      type: typeof r["type"] === "string" ? r["type"] : base[k].type,
      enabled: r["enabled"] === true,
      min: typeof r["min"] === "number" ? r["min"] : base[k].min,
      max: typeof r["max"] === "number" ? r["max"] : base[k].max,
    };
  };
  const pv = o["player"];
  const pr = pv && typeof pv === "object" ? (pv as Record<string, unknown>) : {};
  const hostIds = Array.isArray(pr["hostIds"]) ? pr["hostIds"].filter((h): h is number => typeof h === "number") : null;
  return {
    role: typeof o["role"] === "string" ? o["role"] : "",
    label: typeof o["label"] === "string" ? o["label"] : base.label,
    type: typeof o["type"] === "string" ? o["type"] : base.type,
    active: o["active"] === true,
    player: {
      type: FilterType.PLAYER,
      enabled: pr["enabled"] === true,
      hostIds,
      unhosted: pr["unhosted"] === true,
      carded: pr["carded"] === true,
      uncarded: pr["uncarded"] === true,
    },
    duration: range("duration"),
    gains: range("gains"),
    bet: range("bet"),
    averageBet: range("averageBet"),
    theo: range("theo"),
    filters: Array.isArray(o["filters"]) ? (o["filters"] as unknown[]).filter(isFilterClause) : [],
  };
}

export function isFilterClause(v: unknown): v is IFilter {
  if (!v || typeof v !== "object") return false;
  const c = v as Record<string, unknown>;
  const dataTypes = ["string", "number", "bool", "datetime"];
  return (
    typeof c["field"] === "string" &&
    typeof c["value"] === "string" &&
    typeof c["type"] === "string" &&
    dataTypes.includes(c["valueDataType"] as string) &&
    (c["condition"] === null || c["condition"] === "" || c["condition"] === "AND" || c["condition"] === "OR")
  );
}

// ONE SLIDER STEP IS 1; VALUES ARE CLAMPED INTO FLOOR..CEIL AND KEPT LOW <= HIGH
export function clampRange(floor: number, ceil: number, min: number, max: number): [number, number] {
  const lo = Math.min(ceil, Math.max(floor, Math.round(min)));
  const hi = Math.min(ceil, Math.max(floor, Math.round(max)));
  return lo <= hi ? [lo, hi] : [hi, lo];
}
